import { Person } from "./Configuration";

export interface ChargingType {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Accessory {
  id: number;
  fi: string;
  en: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ImageVariant {
  url: string;
  width: number;
  height: number;
}

export interface Image {
  ID: string;
  imageableId: number;
  imageableType: string;
  nettixImageId: number;
  userId: number;
  customerId: number;
  siteId: number;
  position: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  image: string | null;
}

export const dummyUser: Person = {
  name: "John Doe",
  title: "Sales Manager",
  email: "john.doe@example.com",
  phone: "123-456-7890",
  imageUrl: "",
};

export interface PublicStaticCarInfo {
  vehicleType: string;
  make: string;
  model: string;

  modelTypeName?: string | null;
  bodyType?: string | null;

  powerHp?: number | null;
  yearOfRegistration: number;
  fuelType: string;

  registrationNumber?: string | null;
  taxFree?: boolean | null;
  vin?: string | null;

  condition?: string | null;
  transmission?: string | null;
  fuelEfficiency?: string | null;

  color?: string | null;
  colorType?: string | null;
  driveType?: string | null;
  gearType?: string | null;
  engineSize?: number | null;

  accessories: Accessory[];
  chargingTypes: ChargingType[];

  // Nettix extra fields
  chargingType?: string | null; // deprecated in backend but still present
  seats?: number | null;
  doors?: number | null;
  steeringWheelLeft?: boolean | null;

  batteryCapacity?: number | null;
  batteryGuaranteeMonth?: number | null;
  batteryGuaranteeKm?: number | null;
  electricRange?: number | null;

  torque?: number | null;
  co2Emission?: number | null;
  topSpeed?: number | null;
  acceleration?: number | null;

  consumptionUrban?: number | null;
  consumptionRoad?: number | null;
  consumptionCombined?: number | null;

  electricConsumptionUrban?: number | null;
  electricConsumptionRoad?: number | null;
  electricConsumptionCombined?: number | null;

  curbWeight?: number | null;
  grossWeight?: number | null;
  towWeightWithoutBrakes?: number | null;

  chargingPower?: number | null;
  maximumChargingPower?: number | null;
}

/** Embedded: PublicDynamicCarInfoResponse */
export interface PublicDynamicCarInfo {
  country: string;
  region: string;
  town: string;
  streetAddress: string;
  accessories: Accessory[];

  mileage: number;
  briefDescription: string;
  uspInfo: string;

  images: Image[];
  roadWorthy?: boolean | null;
}

/** Your requested interface: PublicCarInStockResponse + embedded structs */
export interface Car extends PublicStaticCarInfo, PublicDynamicCarInfo {
  ID: number;

  nettiautoId?: string | null;
  createdAt: string; // Go time.Time -> ISO string in JSON
  updatedAt: string;
  user ?: User; // Simplified for this example
  site ?: any; // Simplified for this example
  status: string;

  price: number;
  deliveryCost?: number | null;

  labels: any[];
}

export const fieldTranslationMap: { [key in keyof Car]: string } = {
  
  // Basic info

  vehicleType: "Ajoneuvotyyppi",
  make: "Merkki",
  model: "Malli",
  modelTypeName: "Mallitarkenne",
  bodyType: "Korimalli",

  powerHp: "Teho (hv)",
  yearOfRegistration: "Käyttöönottovuosi",
  fuelType: "Polttoaine",

  registrationNumber: "Rekisterinumero",
  taxFree: "ALV-vähennyskelpoinen",
  vin: "VIN-numero",

  condition: "Kunto",
  transmission: "Vaihteisto",
  fuelEfficiency: "Kulutus",

  color: "Väri",
  colorType: "Värityyppi",
  driveType: "Vetotapa",
  gearType: "Vaihteisto",
  engineSize: "Moottorin tilavuus",

  accessories: "Varusteet",
  chargingTypes: "Lataustyypit",
  chargingType: "Lataustyyppi",

  seats: "Istuinten määrä",
  doors: "Ovien määrä",
  steeringWheelLeft: "Ohjaus vasemmalla",

  // Electric / battery
  batteryCapacity: "Akun kapasiteetti (kWh)",
  batteryGuaranteeMonth: "Akkutakuu (kk)",
  batteryGuaranteeKm: "Akkutakuu (km)",
  electricRange: "Sähköinen toimintamatka (km)",

  // Performance
  torque: "Vääntömomentti (Nm)",
  co2Emission: "CO₂-päästöt",
  topSpeed: "Huippunopeus (km/h)",
  acceleration: "Kiihtyvyys (0–100 km/h)",

  // Consumption
  consumptionUrban: "Kulutus (kaupunki)",
  consumptionRoad: "Kulutus (maantie)",
  consumptionCombined: "Kulutus (yhdistetty)",

  electricConsumptionUrban: "Sähkönkulutus (kaupunki)",
  electricConsumptionRoad: "Sähkönkulutus (maantie)",
  electricConsumptionCombined: "Sähkönkulutus (yhdistetty)",

  // Weights
  curbWeight: "Omamassa",
  grossWeight: "Kokonaismassa",
  towWeightWithoutBrakes: "Vetomassa ilman jarruja",

  // Charging
  chargingPower: "Latausteho (kW)",
  maximumChargingPower: "Maksimi latausteho (kW)",

  // Location
  country: "Maa",
  region: "Alue",
  town: "Kaupunki",
  streetAddress: "Katuosoite",

  // Listing content
  mileage: "Mittarilukema (km)",
  briefDescription: "Lyhyt kuvaus",
  uspInfo: "Myyntivaltit",
  images: "Kuvat",
  roadWorthy: "Tieliikennekelpoinen",

  // Meta
  ID: "ID",
  nettiautoId: "Nettiauto ID",
  createdAt: "Luotu",
  updatedAt: "Päivitetty",
  status: "Tila",

  price: "Hinta",
  deliveryCost: "Toimituskulut",
  labels: "Tunnisteet",
};


// Dummy car example
export const dummyCar: Car = {
  ID: 1,
  nettiautoId: "NET123456",
  createdAt: "2024-01-15T10:30:00Z",
  updatedAt: "2024-01-15T10:30:00Z",
  status: "available",
  price: 25000,
  deliveryCost: 500,
  labels: [],

  // Static car info
  vehicleType: "Car",
  make: "Toyota",
  model: "Corolla",
  modelTypeName: "Hybrid",
  bodyType: "Sedan",
  powerHp: 122,
  yearOfRegistration: 2022,
  fuelType: "Hybrid",
  registrationNumber: "ABC-123",
  taxFree: false,
  vin: "1HGBH41JXMN109186",
  condition: "Used",
  transmission: "Automatic",
  fuelEfficiency: "4.5L/100km",
  color: "Silver",
  colorType: "Metallic",
  driveType: "Front-wheel drive",
  gearType: "CVT",
  engineSize: 1.8,
  accessories: [
    { id: 1, fi: "Ilmastointi", en: "Air Conditioning", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    { id: 2, fi: "Navigointi", en: "Navigation System", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    { id: 3, fi: "Sähköikkunat", en: "Power Windows", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    { id: 4, fi: "Bluetooth", en: "Bluetooth", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    { id: 5, fi: "Peruutuskamera", en: "Rearview Camera", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    {id: 6, fi: "Vakionopeudensäädin", en: "Cruise Control", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    {id: 7, fi: "Sadetunnistin", en: "Rain Sensor", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    {id: 8, fi: "Nahkaverhoilu", en: "Leather Upholstery", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    {id: 9, fi: "LED-ajovalot", en: "LED Headlights", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
    {id: 10, fi: "Automaattinen hätäjarrutus", en: "Automatic Emergency Braking", createdAt: "2024-01-01T00:00:00Z", updatedAt: "2024-01-01T00:00:00Z" },
  ],
  chargingTypes: [],
  chargingType: null,
  seats: 5,
  doors: 4,
  steeringWheelLeft: true,
  batteryCapacity: null,
  batteryGuaranteeMonth: null,
  batteryGuaranteeKm: null,
  electricRange: null,
  torque: 142,
  co2Emission: 102,
  topSpeed: 180,
  acceleration: 10.9,
  consumptionUrban: 4.2,
  consumptionRoad: 4.8,
  consumptionCombined: 4.5,
  electricConsumptionUrban: null,
  electricConsumptionRoad: null,
  electricConsumptionCombined: null,
  curbWeight: 1405,
  grossWeight: 1850,
  towWeightWithoutBrakes: 450,
  chargingPower: null,
  maximumChargingPower: null,

  // Dynamic car info
  country: "Finland",
  region: "Uusimaa",
  town: "Helsinki",
  streetAddress: "Mannerheimintie 123",
  mileage: 45000,
  briefDescription: "Well-maintained Toyota Corolla Hybrid in excellent condition. Perfect for city driving and commuting.",
  uspInfo: "Low fuel consumption, reliable, full service history",
  images: [],
  roadWorthy: true,
};


